import { LEGACY_SAVE_VERSIONS, PREVIOUS_SAVE_VERSION, SAVE_VERSION } from '../config/balancing';
import { BUILD_CATALOG } from '../config/catalog';
import { PARCELS, SLOTS } from '../config/map';
import { isKnownLandId, migrateLegacyLandIds } from '../spatial/plots';
import type {
  EntityKind,
  FloorPaint,
  FloorStyleId,
  ParkState,
  PlacedEntity,
  RotationQuarter,
  SaveData,
  SubcellAnchor,
} from '../types';
import { parseEntityAppearance } from './appearance';
import { maxLevel } from './economy';
import { FLOOR_STYLE_IDS } from './floors';
import { createInitialState } from './park';
import { migrateLegacyPlacedEntity } from './placement';
import { ROTATION_QUARTERS } from '../spatial/footprints';

const KNOWN_PARCEL_IDS = new Set<string>(PARCELS.map((parcel) => parcel.id));
const KNOWN_SLOT_IDS = new Set<string>(SLOTS.map((slot) => slot.id));
const KNOWN_KINDS = new Set<EntityKind>(BUILD_CATALOG.map((item) => item.kind));
const KNOWN_FLOOR_STYLES = new Set<FloorStyleId>([...FLOOR_STYLE_IDS, 'grass']);

/** Historical V1→V2 cutover key. Never overwrite or delete in production. */
export const SAVE_MIGRATION_BACKUP_KEY = 'park_v1_col007_v1_backup';
/** Immediate pre-V3 raw payload. Independent of historical V1/V2 backup keys. */
export const SAVE_V3_MIGRATION_BACKUP_KEY = 'park_v1_pre_v3_backup_v2';

const memoryBackup = new Map<string, string>();

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isRotationQuarter(value: unknown): value is RotationQuarter {
  return typeof value === 'number' && (ROTATION_QUARTERS as readonly number[]).includes(value);
}

function parseAnchor(value: unknown): SubcellAnchor | null {
  if (!isRecord(value)) {
    return null;
  }
  const { sx, sz } = value;
  if (typeof sx !== 'number' || !Number.isInteger(sx) || typeof sz !== 'number' || !Number.isInteger(sz)) {
    return null;
  }
  return { sx, sz };
}

function parseFloorPaint(value: unknown): FloorPaint | null {
  if (!isRecord(value)) {
    return null;
  }
  const { sx, sz, style } = value;
  if (typeof sx !== 'number' || !Number.isInteger(sx) || typeof sz !== 'number' || !Number.isInteger(sz)) {
    return null;
  }
  if (typeof style !== 'string' || !KNOWN_FLOOR_STYLES.has(style as FloorStyleId)) {
    return null;
  }
  return { sx, sz, style: style as FloorStyleId };
}

function parseEntity(value: unknown, saveVersion: number): PlacedEntity | null {
  if (!isRecord(value)) {
    return null;
  }
  const { id, kind, slotId, level, storedCoins } = value;
  if (typeof id !== 'string' || id.length === 0) {
    return null;
  }
  if (typeof kind !== 'string' || !KNOWN_KINDS.has(kind as EntityKind)) {
    return null;
  }
  if (typeof slotId !== 'string' || !KNOWN_SLOT_IDS.has(slotId)) {
    return null;
  }
  if (typeof level !== 'number' || !Number.isInteger(level) || level < 1) {
    return null;
  }
  if (level > maxLevel(kind as EntityKind)) {
    return null;
  }
  if (typeof storedCoins !== 'number' || !Number.isFinite(storedCoins) || storedCoins < 0) {
    return null;
  }
  const appearance = parseEntityAppearance(kind as EntityKind, value.appearance);
  const base = {
    id,
    kind: kind as EntityKind,
    slotId,
    level,
    storedCoins: Math.floor(storedCoins),
    ...(appearance ? { appearance } : {}),
  };
  if (value.rotation != null && !isRotationQuarter(value.rotation)) {
    return null;
  }
  if (value.anchor != null && !parseAnchor(value.anchor)) {
    return null;
  }
  // V1 (and any save missing an anchor) uses slot-center migration.
  // V2 already persisted exact anchors — remigrating would collapse moved entities.
  if (saveVersion < 2 || value.anchor == null) {
    const migrated = migrateLegacyPlacedEntity(base);
    if (isRotationQuarter(value.rotation)) {
      migrated.rotation = value.rotation;
    }
    return migrated;
  }
  const anchor = parseAnchor(value.anchor);
  if (!anchor) {
    return null;
  }
  return { ...base, anchor, rotation: (value.rotation as RotationQuarter | undefined) ?? 0 };
}

function builtKindMilestonesForSave(state: ParkState): EntityKind[] {
  const milestones: EntityKind[] = [];
  for (const kind of state.builtKindMilestones ?? []) {
    if (!milestones.includes(kind)) {
      milestones.push(kind);
    }
  }
  // A currently placed entity is definitive evidence that its milestone was achieved.
  for (const entity of state.entities) {
    if (!milestones.includes(entity.kind)) {
      milestones.push(entity.kind);
    }
  }
  return milestones;
}

export function serializePark(state: ParkState): string {
  const save: SaveData = {
    saveVersion: SAVE_VERSION,
    coins: state.coins,
    unlockedLandIds: [...state.unlockedLandIds],
    entities: state.entities.map((entity) => {
      const migrated = entity.anchor && entity.rotation != null ? entity : migrateLegacyPlacedEntity(entity);
      return {
        ...migrated,
        anchor: { sx: migrated.anchor!.sx, sz: migrated.anchor!.sz },
        rotation: migrated.rotation ?? 0,
      };
    }),
    builtKindMilestones: builtKindMilestonesForSave(state),
    lastSaveTimestamp: state.lastSaveTimestamp,
    ...(state.floors && state.floors.length > 0
      ? { floors: state.floors.map((cell) => ({ ...cell })) }
      : {}),
  };
  return JSON.stringify(save);
}

export function peekSaveVersion(raw: string | null | undefined): number | null {
  if (!raw) {
    return null;
  }
  try {
    const data: unknown = JSON.parse(raw);
    if (!isRecord(data) || typeof data.saveVersion !== 'number') {
      return null;
    }
    return data.saveVersion;
  } catch {
    return null;
  }
}

function readBackupKey(key: string): string | null {
  try {
    if (typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem(key);
      if (stored != null) {
        return stored;
      }
    }
  } catch {
    // ignore
  }
  return memoryBackup.get(key) ?? null;
}

function retainBackupKey(key: string, raw: string): void {
  if (memoryBackup.has(key) || readBackupKey(key) != null) {
    return;
  }
  memoryBackup.set(key, raw);
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem(key) == null) {
      localStorage.setItem(key, raw);
    }
  } catch {
    // Ignore quota / private-mode failures.
  }
}

function clearBackupKey(key: string): void {
  memoryBackup.delete(key);
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(key);
    }
  } catch {
    // ignore
  }
}

/** Persist the immediate pre-V3 raw save. Never overwrites an existing V3 backup or historical keys. */
export function retainMigrationBackup(raw: string): void {
  retainBackupKey(SAVE_V3_MIGRATION_BACKUP_KEY, raw);
}

export function readMigrationBackup(): string | null {
  return readBackupKey(SAVE_V3_MIGRATION_BACKUP_KEY);
}

export function readHistoricalMigrationBackup(): string | null {
  return readBackupKey(SAVE_MIGRATION_BACKUP_KEY);
}

/** Test/dev helper: seed a backup key without going through retain (does not overwrite). */
export function seedMigrationBackup(key: string, raw: string): void {
  retainBackupKey(key, raw);
}

export function clearMigrationBackup(): void {
  clearBackupKey(SAVE_V3_MIGRATION_BACKUP_KEY);
  clearBackupKey(SAVE_MIGRATION_BACKUP_KEY);
}

/** Parse a canonical save without inventing a fresh park. Null means incompatible. */
export function tryParseSave(raw: string): ParkState | null {
  try {
    const data: unknown = JSON.parse(raw);
    if (!isRecord(data) || typeof data.saveVersion !== 'number') {
      return null;
    }
    const supported = data.saveVersion === SAVE_VERSION || data.saveVersion === PREVIOUS_SAVE_VERSION || (LEGACY_SAVE_VERSIONS as readonly number[]).includes(data.saveVersion);
    if (!supported) {
      return null;
    }
    const saveVersion = data.saveVersion;
    if (typeof data.coins !== 'number' || !Number.isFinite(data.coins) || data.coins < 0) {
      return null;
    }
    if (!Array.isArray(data.unlockedLandIds) || data.unlockedLandIds.length === 0) {
      return null;
    }
    const unlockedLandIds: string[] = [];
    for (const id of data.unlockedLandIds) {
      if (typeof id !== 'string' || (!KNOWN_PARCEL_IDS.has(id) && !isKnownLandId(id))) {
        return null;
      }
      if (!unlockedLandIds.includes(id)) {
        unlockedLandIds.push(id);
      }
    }
    const migratedLandIds = migrateLegacyLandIds(unlockedLandIds);
    if (migratedLandIds.length === 0) {
      return null;
    }
    if (!Array.isArray(data.entities)) {
      return null;
    }
    const entities: PlacedEntity[] = [];
    const usedIds = new Set<string>();
    for (const item of data.entities) {
      const entity = parseEntity(item, saveVersion);
      if (!entity) {
        return null;
      }
      if (usedIds.has(entity.id)) {
        return null;
      }
      usedIds.add(entity.id);
      entities.push(entity);
    }
    const builtKindMilestones: EntityKind[] = [];
    if (data.builtKindMilestones !== undefined) {
      if (!Array.isArray(data.builtKindMilestones)) {
        return null;
      }
      for (const item of data.builtKindMilestones) {
        if (typeof item !== 'string' || !KNOWN_KINDS.has(item as EntityKind)) {
          return null;
        }
        const kind = item as EntityKind;
        if (!builtKindMilestones.includes(kind)) {
          builtKindMilestones.push(kind);
        }
      }
    }
    // Backward-compatible seed for older SAVE_VERSION 3 payloads that predate milestones.
    for (const entity of entities) {
      if (!builtKindMilestones.includes(entity.kind)) {
        builtKindMilestones.push(entity.kind);
      }
    }
    if (typeof data.lastSaveTimestamp !== 'number' || !Number.isFinite(data.lastSaveTimestamp)) {
      return null;
    }
    let floors: FloorPaint[] | undefined;
    if (data.floors !== undefined) {
      if (!Array.isArray(data.floors)) {
        return null;
      }
      floors = [];
      const usedCells = new Set<string>();
      for (const item of data.floors) {
        const cell = parseFloorPaint(item);
        if (!cell) {
          return null;
        }
        const key = `${cell.sx},${cell.sz}`;
        if (usedCells.has(key)) {
          return null;
        }
        usedCells.add(key);
        floors.push(cell);
      }
    }
    return {
      coins: Math.floor(data.coins),
      unlockedLandIds: migratedLandIds,
      entities,
      builtKindMilestones,
      lastSaveTimestamp: data.lastSaveTimestamp,
      ...(floors && floors.length > 0 ? { floors } : {}),
    };
  } catch {
    return null;
  }
}

export function parseSave(raw: string | null | undefined, now = Date.now()): ParkState {
  if (!raw) {
    return createInitialState(now);
  }
  return tryParseSave(raw) ?? createInitialState(now);
}
